import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import api from "../../services/api";

const POLL_INTERVAL_MS = 60_000; // 60 seconds

/* ─── Helper: status badge ─────────────────────────────── */
function StatusBadge({ status }) {
  const map = {
    Pending:   "bg-yellow-100 text-yellow-700",
    Replied:   "bg-blue-100 text-blue-700",
    Completed: "bg-green-100 text-green-700",
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[status] || "bg-gray-100 text-gray-500"}`}>
      {status}
    </span>
  );
}

/* ─── Helper: type badge ───────────────────────────────── */
function TypeBadge({ type }) {
  const map = {
    Appointment: "bg-indigo-100 text-indigo-700",
    Enquiry:     "bg-purple-100 text-purple-700",
    Online:      "bg-teal-100 text-teal-700",
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[type] || "bg-gray-100 text-gray-500"}`}>
      {type}
    </span>
  );
}

/* ─── Reply Modal ─────────────────────────────────────── */
function ReplyModal({ request, onClose, onSaved }) {
  const [reply,  setReply]  = useState(request.adminReply || "");
  const [status, setStatus] = useState(request.status   || "Pending");
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await api.put(`/requests/${request._id}`, { adminReply: reply, status });
      onSaved();
      onClose();
    } catch {
      setErr("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">📝 Reply to Request</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        {/* Request summary */}
        <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-1">
          <p><span className="font-semibold text-gray-600">Name:</span> {request.name}</p>
          <p><span className="font-semibold text-gray-600">Phone:</span> {request.phone}</p>
          {request.message && (
            <p><span className="font-semibold text-gray-600">Message:</span> {request.message}</p>
          )}
          {request.appointmentDate && (
            <p><span className="font-semibold text-gray-600">Date:</span> {request.appointmentDate}</p>
          )}
          {request.preferredTime && (
            <p><span className="font-semibold text-gray-600">Time:</span> {request.preferredTime}</p>
          )}
          {request.doctorName && (
            <p><span className="font-semibold text-gray-600">Doctor:</span> Dr. {request.doctorName}</p>
          )}
          {request.doctorProfileName && (
            <p><span className="font-semibold text-gray-600">Doctor:</span> Dr. {request.doctorProfileName}</p>
          )}
        </div>

        {/* Status selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="Pending">Pending</option>
            <option value="Replied">Replied</option>
            <option value="Completed">Completed</option>
          </select>
        </div>

        {/* Admin reply */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Admin Reply</label>
          <textarea
            rows={4}
            placeholder="Type your reply to the patient here…"
            value={reply}
            onChange={e => setReply(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-y"
          />
        </div>

        {err && <p className="text-red-500 text-sm">{err}</p>}

        <div className="flex gap-3 pt-1">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-60 transition"
          >
            {saving ? "Saving…" : "Save Reply"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ──────────────────────────────────── */
export default function AdminDashboard() {
  const [requests,   setRequests]   = useState([]);
  const [loading,    setLoading]    = useState(true);   // initial skeleton only
  const [refreshing, setRefreshing] = useState(false);  // silent background refresh
  const [lastFetch,  setLastFetch]  = useState(null);   // Date of most recent successful fetch
  const [newIds,     setNewIds]     = useState(new Set()); // IDs received after first load
  const [filter,     setFilter]     = useState("All");  // All | Appointment | Enquiry | Online
  const [statusF,    setStatusF]    = useState("All");  // All | Pending | Replied | Completed
  const [search,     setSearch]     = useState("");
  const [doctorF,    setDoctorF]    = useState("");      // filter by doctor name
  const [selected,   setSelected]   = useState(null);   // request being replied to

  const isFirstLoad  = useRef(true);
  const intervalRef  = useRef(null);
  const prevIdsRef   = useRef(null); // Set<string> of IDs from the previous fetch

  /* ── core fetch ─────────────────────────────────────── */
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    else         setRefreshing(true);
    try {
      const res  = await api.get("/requests");
      const data = res.data || [];

      // On every fetch after the first, diff against previous IDs
      // to highlight truly new rows.
      if (!isFirstLoad.current && prevIdsRef.current) {
        const incoming = new Set(data.map(r => r._id));
        const fresh    = [...incoming].filter(id => !prevIdsRef.current.has(id));
        if (fresh.length) setNewIds(prev => new Set([...prev, ...fresh]));
      }

      prevIdsRef.current = new Set(data.map(r => r._id));
      isFirstLoad.current = false;

      setRequests(data);
      setLastFetch(new Date());
    } catch {
      /* silent — auth interceptor handles 401 */
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  /* ── initial load ───────────────────────────────────── */
  useEffect(() => { load(); }, [load]);

  /* ── polling — paused while reply modal is open ─────── */
  useEffect(() => {
    function startPolling() {
      intervalRef.current = setInterval(() => {
        // Don't poll while admin is mid-edit in the reply modal
        if (selected) return;
        load({ silent: true });
      }, POLL_INTERVAL_MS);
    }
    function stopPolling() {
      clearInterval(intervalRef.current);
    }

    startPolling();
    return stopPolling;
  // Re-register when `selected` or `load` changes so the closure stays fresh
  }, [selected, load]);

  /* ── pause polling when tab is hidden ───────────────── */
  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState === "visible") {
        // Came back to tab — fetch immediately then let interval resume
        load({ silent: true });
      }
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [load]);

  /* ── Stats ── */
  const stats = useMemo(() => ({
    total:       requests.length,
    pending:     requests.filter(r => r.status === "Pending").length,
    appointments:requests.filter(r => r.type   === "Appointment").length,
    enquiries:   requests.filter(r => r.type   === "Enquiry").length,
    online:      requests.filter(r => r.type   === "Online").length,
  }), [requests]);

  /* ── Filtered list ── */
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const dq = doctorF.toLowerCase();
    return requests.filter(r => {
      if (filter !== "All" && r.type   !== filter)   return false;
      if (statusF !== "All" && r.status !== statusF) return false;
      if (q && !r.name?.toLowerCase().includes(q) && !r.phone?.includes(q)) return false;
      if (dq) {
        const docName = (r.doctorName || r.doctorProfileName || "").toLowerCase();
        if (!docName.includes(dq)) return false;
      }
      return true;
    });
  }, [requests, filter, statusF, search, doctorF]);

  const TAB = (label, value, count) => (
    <button
      key={value}
      onClick={() => setFilter(value)}
      className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
        filter === value
          ? "bg-green-600 text-white"
          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
      }`}
    >
      {label} {count > 0 && <span className="ml-1 opacity-80">({count})</span>}
    </button>
  );

  return (
    <div className="space-y-6 max-w-6xl">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">📋 OPD Requests</h1>
          <p className="text-sm text-gray-500 mt-1">
            View and manage all appointment requests, enquiries, and online consultations.
          </p>
        </div>

        {/* Refresh controls */}
        <div className="flex flex-col items-end gap-1 shrink-0 pt-1">
          <button
            onClick={() => load({ silent: true })}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
          >
            <span
              style={{
                display: "inline-block",
                transition: "transform 0.4s",
                transform: refreshing ? "rotate(360deg)" : "none",
              }}
            >
              ↻
            </span>
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          {lastFetch && (
            <span className="text-xs text-gray-400">
              Updated {lastFetch.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              {" · "}auto every 60 s
            </span>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: "Total",        value: stats.total,        color: "bg-gray-50    text-gray-700"   },
          { label: "Pending",      value: stats.pending,      color: "bg-yellow-50  text-yellow-700" },
          { label: "Appointments", value: stats.appointments, color: "bg-indigo-50  text-indigo-700" },
          { label: "Enquiries",    value: stats.enquiries,    color: "bg-purple-50  text-purple-700" },
          { label: "Online",       value: stats.online,       color: "bg-teal-50    text-teal-700"   },
        ].map(s => (
          <div key={s.label} className={`${s.color} rounded-2xl p-4 text-center border border-gray-100`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs text-gray-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        {TAB("All",          "All",         requests.length)}
        {TAB("Appointments", "Appointment", stats.appointments)}
        {TAB("Enquiries",    "Enquiry",     stats.enquiries)}
        {TAB("Online",       "Online",      stats.online)}

        <div className="ml-auto flex gap-2 flex-wrap">
          {/* Status filter */}
          <select
            value={statusF}
            onChange={e => setStatusF(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="All">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Replied">Replied</option>
            <option value="Completed">Completed</option>
          </select>

          {/* Doctor filter */}
          <input
            type="text"
            placeholder="Filter by doctor…"
            value={doctorF}
            onChange={e => setDoctorF(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 w-40"
          />

          {/* Search */}
          <input
            type="text"
            placeholder="Search name or phone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 w-48"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <p className="text-center py-16 text-gray-400 text-sm">Loading requests…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-3">📭</p>
            <p className="text-sm font-medium">No requests found</p>
            <p className="text-xs mt-1">Try adjusting the filters above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {["Patient", "Phone", "Type", "Date / Time", "Doctor", "Status", "Actions"].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(r => (
                  <tr
                    key={r._id}
                    className={`hover:bg-gray-50 transition ${newIds.has(r._id) ? "bg-green-50" : ""}`}
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        {newIds.has(r._id) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700 shrink-0">
                            New
                          </span>
                        )}
                        <div>
                          <p className="font-medium text-gray-800">{r.name}</p>
                          {r.message && (
                            <p className="text-xs text-gray-400 truncate max-w-[160px]" title={r.message}>
                              {r.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-gray-500">{r.phone}</td>
                    <td className="px-5 py-3.5">
                      <TypeBadge type={r.type} />
                      {r.type === "Online" && r.paymentStatus === "paid" && (
                        <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-medium">💳 Paid</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-gray-500 whitespace-nowrap">
                      {r.appointmentDate || "—"}
                      {r.preferredTime && <span className="block text-xs text-gray-400">{r.preferredTime}</span>}
                    </td>
                    <td className="px-5 py-3.5 text-gray-500">
                      {r.doctorName || r.doctorProfileName
                        ? `Dr. ${r.doctorName || r.doctorProfileName}`
                        : "—"}
                    </td>
                    <td className="px-5 py-3.5">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        onClick={() => {
                          setSelected(r);
                          // Opening the modal counts as "seen" — clear the new badge
                          setNewIds(prev => {
                            const next = new Set(prev);
                            next.delete(r._id);
                            return next;
                          });
                        }}
                        className="text-blue-500 hover:text-blue-700 text-xs font-medium"
                      >
                        {r.adminReply ? "Edit Reply" : "Reply"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reply modal */}
      {selected && (
        <ReplyModal
          request={selected}
          onClose={() => setSelected(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}